<template>
  <div class="w-full h-full flex flex-col overflow-auto overflow-x-hidden">
    <div class="flex p-8 pt-4 items-center gap-4 border-b">
      <div class="min-w-10 w-10 h-10 overflow-hidden rounded-xl">
        <img src="/images/logo.png" alt="" />
      </div>
      <div
        class="text-xl title-color whitespace-nowrap duration-300"
        :class="{ 'md:opacity-0': collapsed }"
      >
        Rapidoo Admin
      </div>
    </div>
    <div class="py-2"></div>
    <div class="tourguidEl menuNavs">
      <div
        class="p-4 py-0"
        v-for="(nav, i) in filteredNavs"
        :key="i"
        :permission="nav.permission || 'none'"
      >
        <component
          :is="nav.name ? 'router-link' : 'div'"
          :to="{ name: nav.name }"
          class="flex p-4 items-center gap-4 ripple rounded-full"
          @click="selectNav(nav)"
          :class="[nav.class || '', { 'bg-slate-100 ': isActive(nav) }]"
        >
          <div class="min-w-6 w-6 h-6 relative">
            <ColorImg
              :src="nav.icon"
              alt=""
              class="bg-[#3c3c3c] w-full h-full"
            />
            <span
              v-if="getNavBadge(nav) > 0 && collapsed"
              class="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-0.5 flex items-center justify-center border border-white pointer-events-none"
            >
              {{ getNavBadge(nav) > 9 ? '9+' : getNavBadge(nav) }}
            </span>
          </div>
          <div
            class="title-color whitespace-nowrap duration-300 flex-1 flex items-center justify-between gap-2 overflow-hidden"
            :class="{ 'md:opacity-0': collapsed }"
          >
            <span>{{ nav.text }}</span>
            <span
              v-if="getNavBadge(nav) > 0"
              class="bg-red-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-[20px] px-1.5 flex items-center justify-center shadow-sm pointer-events-none"
            >
              {{ getNavBadge(nav) > 99 ? '99+' : getNavBadge(nav) }}
            </span>
          </div>
        </component>
      </div>
    </div>
    <div class="py-8"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import Translator from '@/shared/utils/translator'
import setting_grayIcon from './assets/images/icons/setting-gray.svg'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import AuthService from '@/features/auth/authService'
import { chatState } from '@/features/chat/chatState.js'

const emit = defineEmits(['navClicked'])
defineProps({ collapsed: { type: Boolean, default: true } })
const route = useRoute()

function getNavBadge(nav) {
  if (nav?.name === 'customers') {
    return chatState.customersUnreadCount || 0
  }
  if (nav?.name === 'riders') {
    return chatState.ridersUnreadCount || 0
  }
  return 0
}

const navs = [
  // Vue d'ensemble
  {
    text: Translator.$t('navs.home'),
    icon: '/images/icons/navs/diagramme.png',
    name: 'dashboard',
    class: 'tourguidEl dashboard-menu',
    permission: 'dashboard',
  },
  // Clients
  {
    text: Translator.$t('navs.customers'),
    icon: '/images/icons/navs/customer-review.png',
    name: 'customers',
    class: 'mt-4',
    permission: 'customers',
  },
  {
    text: Translator.$t('navs.milestones'),
    icon: '/images/icons/navs/la-mise-en-reseau.png',
    name: 'milestones',
    permission: 'milestones',
  },
  // Restaurant
  {
    text: Translator.$t('navs.restaurants'),
    icon: '/images/icons/navs/restaurant.png',
    name: 'restaurants',
    class: 'tourguidEl dashboard-menu mt-4',
    permission: 'restaurants',
  },
  {
    text: Translator.$t('navs.dishes'),
    icon: '/images/icons/navs/dish.svg',
    name: 'dishes',
    permission: 'restaurants',
  },
  {
    text: Translator.$t('navs.categories'),
    icon: '/images/icons/navs/boisson-non-alcoolisee.png',
    name: 'categories',
    permission: 'restaurants',
  },
  {
    text: Translator.$t('navs.accompaniements'),
    icon: '/images/icons/navs/bouteille-de-ketchup.png',
    name: 'accompaniements',
    permission: 'restaurants',
  },
  // Livraison
  {
    text: Translator.$t('navs.riders'),
    icon: '/images/icons/navs/livreur.png',
    name: 'riders',
    class: 'mt-4',
    permission: 'riders',
  },
  {
    text: Translator.$t('navs.kyc'),
    icon: '/images/icons/navs/utilisateur.png',
    name: 'kyc',
    permission: 'kyc',
  },


  // Finance
  {
    text: Translator.$t('navs.transactions'),
    icon: '/images/icons/navs/diagramme.png',
    name: 'transactions',
    class: 'mt-4',
    permission: 'history',
  },
  // {
  //   text: Translator.$t('navs.withdrawals'),
  //   icon: '/images/icons/navs/boite-de-livraison.png',
  //   name: 'withdrawals',
  // },
  // Commandes
  {
    text: Translator.$t('navs.orders'),
    icon: '/images/icons/navs/verifier.png',
    name: 'orders',
    // class: 'mt-4',
    permission: 'orders',
  },
  {
    text: Translator.$t('navs.packages'),
    icon: '/images/icons/navs/boite-de-livraison.png',
    name: 'packages',
    permission: 'orders',
  },
  {
    text: "Finances",
    icon: '/images/icons/navs/pourcentage.png',
    name: 'fees',
    permission: 'settings',
  },
  // Marketing
  {
    text: Translator.$t('navs.sponsoring'),
    icon: '/images/icons/navs/bon-deal.png',
    name: 'promotions',
    class: 'mt-4',
    permission: 'notifications',
  },
  {
    text: Translator.$t('navs.coupons'),
    icon: '/images/icons/navs/coupon.png',
    name: 'coupons',
    permission: 'notifications',
  },
  {
    text: Translator.$t('navs.promotions'),
    icon: '/images/icons/navs/bon-deal.png',
    name: 'deals',
    permission: 'notifications',
  },
  {
    text: Translator.$t('navs.ads'),
    icon: '/images/icons/navs/publicite.png',
    name: 'ads',
    permission: 'notifications',
  },
  {
    text: Translator.$t('navs.notification'),
    icon: '/images/icons/navs/notification (1).png',
    name: 'notification',
    permission: 'notifications',
  },
  {
    text: Translator.$t('navs.tickets'),
    icon: '/images/icons/navs/question-client.png',
    name: 'tickets',
    class: 'mt-4',
    permission: 'notifications',
  },
  // Boutique Rapidoo
  {
    text: Translator.$t('navs.adminProducts'),
    icon: '/images/icons/navs/box.svg',
    name: 'adminProducts',
    class: 'mt-4',
    permission: 'adminProducts', // Need to check if this exists or use a fallback
  },
  {
    text: Translator.$t('navs.adminProductPurchases'),
    icon: '/images/icons/navs/verifier.png',
    name: 'adminProductPurchases',
    permission: 'adminProducts',
  },
  // {
  //   text: Translator.$t('navs.adminClientDebts'),
  //   icon: '/images/icons/navs/diagramme.png',
  //   name: 'clientDebts',
  //   permission: 'adminProducts',
  // },
  // Administration
  {
    text: Translator.$t('Équipe'),
    icon: '/images/icons/navs/utilisateur.png',
    name: 'team',
    class: 'mt-4',
    permission: 'team-members',
  },
  {
    text: Translator.$t('navs.profile'),
    icon: setting_grayIcon,
    name: 'profile',
    permission: 'profile',
  },
  {
    class: 'mt-4',
    text: Translator.$t('navs.logout'),
    icon: '/images/icons/navs/en-dehors.png',
    isLogout: true,
  },
]

const filteredNavs = computed(() => {
  const userData = AuthService.getUserData(true)
  if (!userData?.value) return []

  const role = userData.value.role
  if (role === 'super-admin') return navs

  const userPermissions = userData.value.permissions || []

  return navs.filter(nav => {
    if (nav.isLogout) return true
    if (!nav.permission) return true
    if (nav.permission === 'profile') return true // Always accessible

    return userPermissions.includes(nav.permission)
  })
})

function selectNav(nav) {
  if (nav.isLogout) Events.dispatch(EVENTS.LOGOUT_REQUESTED)
  emit('navClicked')
}
function isActive(nav) {
  return route.name == nav?.name
}
</script>

<style>
/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
